import type {
  DeviceColumn,
  DeviceData,
  DeviceComparator,
  ColumnFilter,
  FieldValues,
} from '../components/aditools-device-table';
import Comparator from './comparator.ts';
import EngineeringFilter from './engineering-filter.ts';
import PartialTextFilter from './partial-text-filter.ts';
import PriceFilter from './price-filter.ts';
import MultivalueCheckboxFilter from './multivalue-checkbox-filter.ts';

const TRANSLATIONS = {
  en: {
    partName: 'Part Name',
    showAll: 'Show All',
    blanks: '(Blanks)',
  },
  cn: {
    // partName: '产品型号',
    partName: 'Part Name',
    showAll: '显示全部',
    blanks: '(Blanks)',
  },
  jp: {
    //partName: '製品番号',
    partName: 'Part Name',
    showAll: 'すべて表示',
    blanks: '(Blanks)',
  },
  ru: {
    partName: 'Part Name',
    showAll: 'Show All',
    blanks: '(Blanks)',
  },
  /*
  ru: {
    //partName: 'Наименование компонента',
    partName: 'Part Name',
    showAll: 'Показать все',
  },
  */
};

const setBlankValue = function (deviceData: DeviceData[]): DeviceData[] {
  const deviceDataCopy = deviceData.map((data) => {
    const dataCopy = Object.assign({}, data);
    for (const [key, value] of Object.entries(dataCopy)) {
      if (!key.startsWith('value_')) {
        if (value === '') {
          dataCopy[key] = '-';
        }
      }
    }
    return dataCopy;
  });
  return deviceDataCopy;
};

const NO_FILTER_COLUMNS = ['s25'];

type PSTParameterId = string; // pattern [\d]{1-4}|s[\d]{1-2}

interface PSTParameterDescription {
  name: string; // name of parameter
  definition: string; // what is it?
  type: string; // "Floating Point", "String", "Integer", "Pulldown",?
  filterType: string; // "Sliders", "Textbox", "Checkboxes", ?
  max: string | null; // maximum value - empty string if not applicable
  min: string | null; // minimum value - empty string if not applicable
  hasMagnitude: number; // number used as boolean
  uom: string | null; // unit of measure (V,A, Hz, ?...), or "n/a"
  values: string[] | number[] | null; // the set of allowed values
  classification: string; // Used to group parameters for selection
  minMaxTyp: string | null; // Kind of measure
}

export interface PSTParameterMapping {
  [parmId: string]: PSTParameterDescription;
}

export interface PSTIndex {
  categoryId: string;
  title: string;
  minPartsToRetrieve: string;
  queryStrings: unknown;
  quickFilter: unknown;
  PSTFilter: { cat: unknown | null; pst: unknown | null }[];
  breadcrumburl: { name: string; url: string }[];
  columns: PSTIndexColumn[]; // This is the bulk of the file
}

interface PSTIndexColumn {
  id: string; // "C{index}"
  field: string; // PST parameter ID
  external_name: string; // Used for display
  tooltip: string | null;
  display_in_default_view: string; // 'true', 'false'
  user_displayable: string; // 'true', 'false'
  uom: string | null; // "V", "Hz", "V/rtHz", possibly "n/a"
  defaultUOM: string | null; // "V", "Hz", "V/rtHz", possibly "n/a"
  min: string; // possibly ""
  max: string; // possibly ""
  values: string[] | number[] | null;
  s3type: string | null; // "math", "lookup", ?...
}

interface ParameterData {
  displayValue: string; // value for display with unit suffix
  value: string[]; // raw values - no units
}

interface PartNumberData extends ParameterData {
  dataSheet: number; // Operates as a boolean
  usedInCFTL: number; // Operates as a boolean
  hasAnEvalBoard: number; // Operates as a boolean
  evalBoardUrl: string;
  generic: string; // generic part number
  genericStatus: string;
  companion: unknown[];
}

export interface PSTData {
  categoryId: string;
  description: string;
  data: {
    id: string;
    '0': PartNumberData;
    [parameterId: string]: string | ParameterData; //for each parameter
  }[]; // for each part
}

export function collectDeviceDataFromPST(
  pstData: PSTData,
  mappingData: PSTParameterMapping,
  blanksAsDashes?: boolean,
): DeviceData[] {
  const rows: DeviceData[] = [];
  for (const part of pstData.data) {
    const row: DeviceData = {};
    row['partNumber'] = part['0'].displayValue;
    row['generic'] = part['0'].generic;
    row['id'] = part['0'].displayValue;
    for (const parm in part) {
      if (parm === 'id' || parm === '0' || !(parm in mappingData)) continue;
      const displayKey = 'display_' + parm;
      const valueKey = 'value_' + parm;
      const partparm = part[parm] as ParameterData;
      row[displayKey] = partparm.displayValue;
      const parmdata = mappingData[parm] as PSTParameterDescription; //Covered by guard clause above
      switch (parmdata.type) {
        case 'Floating Point':
          if (partparm.value[0] === '') {
            row[valueKey] = null;
          } else {
            row[valueKey] = parseFloat(partparm.value[0] ?? '0');
          }
          break;
        case 'Integer':
          if (partparm.value[0] === '') {
            row[valueKey] = null;
          } else {
            row[valueKey] = parseInt(partparm.value[0] ?? '0');
          }
          break;

        case 'Pulldown':
          // Nothing in a parameter mapping tells us if a pulldown type on a part is meant to have one value or multiple.
          // The code has to either always return an array or return a single value if that is all that is there.
          // Most pulldowns are single-value and the caller can special-case the few times a multi-value select has a single value.
          // Essentially, this code has optimized for the commonest case, rather than generalizing. Right? Wrong? We'll see.
          if (partparm.value.length === 1) {
            row[valueKey] = partparm.value[0];
          } else {
            row[valueKey] = partparm.value;
          }
          break;
        case 'Date':
          // Date string value is delivered as a Date value. Only s18 uses this so far.
          row[valueKey] = Date.parse(partparm.value[0] ?? '');
          break;
        case 'String':
          row[valueKey] = partparm.value[0];
          break;
        default:
          console.log(`Parameter ${parm} has unknown parameter type ${parmdata.type}`);
          row[valueKey] = partparm.value;
          break;
      }
    }
    rows.push(row);
  }
  return blanksAsDashes ? setBlankValue(rows) : rows;
}

export function collectColumnDataFromPST(
  viewData: PSTIndex,
  mappingData: PSTParameterMapping,
  locale = 'en',
): DeviceColumn<DeviceData>[] {
  const results: DeviceColumn<DeviceData>[] = [];
  viewData.columns.map((item) => {
    const field: PSTParameterId = item.field as PSTParameterId;
    if (mappingData[field]) {
      let columnComparator: DeviceComparator<string>;
      const mappedItem = mappingData[field] as PSTParameterDescription; //Covered by guard clause above
      switch (mappedItem.type) {
        case 'Integer':
          columnComparator = Comparator.integerComparator;
          break;
        case 'Floating Point':
          columnComparator = Comparator.engineeringComparator;
          break;
        case 'Pulldown':
          columnComparator = Comparator.textComparator;
          break;
        case 'Date':
          columnComparator = Comparator.textComparator;
          break;
        case 'String':
          columnComparator = Comparator.textComparator;
          break;
        default:
          columnComparator = Comparator.textComparator;
          break;
      }
      let columnFilter: ColumnFilter<DeviceData> | undefined;
      let columnValues: FieldValues[] = [];
      if (!NO_FILTER_COLUMNS.includes(item.field)) {
        switch (mappedItem.filterType) {
          case 'Sliders': {
            if (mappedItem.uom === '$ US') {
              columnFilter = new PriceFilter('GT');
            } else {
              columnFilter = new EngineeringFilter('GT');
            }
            break;
          }
          case 'Textbox': {
            columnFilter = new PartialTextFilter('');
            break;
          }
          case 'Checkboxes': {
            const fieldValues: FieldValues[] = [];
            if (item.values) {
              fieldValues.push({
                value: '-',
                display: _translations(locale)['blanks'] ?? '(Blanks)',
                match: '',
              });
              item.values.forEach((item) => {
                fieldValues.push({
                  value: item.toString(),
                  display: item.toString(),
                  match: item.toString(),
                });
              });
            }
            columnValues = fieldValues;
            columnFilter = new MultivalueCheckboxFilter(fieldValues);
            break;
          }
          default: {
            columnFilter = new PartialTextFilter('');
            break;
          }
        }
      }

      const column: DeviceColumn<DeviceData> = {
        id: item.field,
        name: mappedItem.name,
        field: 'display_' + item.field,
        sortable: true,
        comparator: columnComparator,
        filter: columnFilter,
        columnValues: columnValues,
        classification: mappedItem.classification,
        isDefault: item.display_in_default_view === 'true',
        unit: mappedItem.uom,
        minMaxTyp: mappedItem.minMaxTyp,
        toolTip: mappedItem.definition ?? mappedItem.name,
      };
      results.push(column);
    } else if (field === '0') {
      // Part Number has no mapping and needs to make a special column for this
      const column: DeviceColumn<DeviceData> = {
        id: '0',
        name: _translations(locale)['partName'] ?? 'Part Name',
        sortable: true,
        comparator: Comparator.partComparator,
        field: 'partNumber',
        filter: new PartialTextFilter('', 'part'),
        columnValues: [],
        classification: 'Other',
        isDefault: true,
        unit: null,
        minMaxTyp: null,
        toolTip: _translations(locale)['partName'] ?? 'Part Name',
      };

      results.push(column);
    }
  });
  return results;
}

export function reduceColumnFilterValuesToThosePresent(
  rowData: DeviceData[],
  columnData: DeviceColumn<DeviceData>[],
): void {
  for (const column of columnData) {
    if (column.columnValues && column.columnValues.length > 0) {
      const fieldValues: FieldValues[] = [];
      for (const columnValue of column.columnValues) {
        const checkData = rowData.filter((x) => {
          const matchValue = typeof columnValue.match !== 'undefined' ? columnValue.match : columnValue.value;
          if (Array.isArray(x[`value_${column.id as string}`])) {
            const xvalues = (x[`${column.field as string}`] as string).split(',').map((s) => s.trim());
            return matchValue !== ''
              ? xvalues.includes(matchValue.trim())
              : xvalues.includes(matchValue.trim()) || xvalues.includes('-');
          } else {
            return matchValue !== ''
              ? x[`${column.field as string}`] === matchValue
              : x[`${column.field as string}`] === matchValue || x[`${column.field as string}`] === '-';
          }
        });
        if (checkData.length > 0) {
          fieldValues.push(columnValue);
        }
      }
      column.filter = new MultivalueCheckboxFilter(fieldValues);
    }
  }
}

export function getPSTName(viewData: PSTIndex): string {
  return viewData.title;
}

function _translations(locale: string): Record<string, string> {
  let loc: 'en' | 'cn' | 'jp' | 'ru' = 'en';
  if (locale === 'en' || locale === 'cn' || locale === 'jp' || locale === 'ru') {
    loc = locale;
  }
  return TRANSLATIONS[loc as 'en' | 'cn' | 'jp' | 'ru'];
}
